import React, { useEffect, useMemo, useState } from "react";

import Image from "next/image";
import NavLink from "../NavLink";
import { Popover } from "@headlessui/react";
import { t } from "@lingui/macro";
import { useActiveWeb3React } from "../../hooks/useActiveWeb3React";
import { useLingui } from "@lingui/react";
import Web3Status from "../Web3Status";
import { useEtherBalance, useTokenBalance } from "../../hooks/useContract";
import {
  convertBigNumberToNormal,
  formatDollarAmount,
  truncate,
} from "../../functions/bigNumber-util";
import { TOKEN_SECOND_ADDRESS } from "../../constants/addresses";
import { isEmpty, isZeroBigNumber } from "../../functions";
import { useBlockNumber } from "../../state/application/hooks";
import { SupportedChainId as ChainId } from "../../constants/chains";
import { useRouter } from "next/router";
import { isNil } from "../../functions";
import ArrowIcon from "../Icons/ArrowIcon";
import { UnsupportedChainIdError, useWeb3React } from "@web3-react/core";
import useResponsive from "../../hooks/useResponsive";
import ThemeToggle from "../ThemeToggle";
import useSwitchNetwork from "../../connectWallet/useSwitchNetwork";

import configs from "../../config";

function AppBar(): JSX.Element {
  const { i18n } = useLingui();
  const { account, chainId, library } = useActiveWeb3React();
  const { error } = useWeb3React();
  const userEthBalance = useEtherBalance();
  const latestBlockNumber = useBlockNumber();
  const { isLg } = useResponsive();
  const [etherBalance, setEtherBalance] = useState();

  const { isWrongNetwork, changeNetwork } = useSwitchNetwork();

  const [balanceSECOND, setBalanceSECOND] = useState();

  const isMetaMask = library && library.provider.isMetaMask;
  const hasSecondBalance = !isEmpty(convertBigNumberToNormal(balanceSECOND, 18));
  const hasEtherBalance = account && chainId && !isNil(etherBalance);
  const balanceSECONDContract = useTokenBalance(TOKEN_SECOND_ADDRESS[chainId]);

  const info = async () => {
    const defaultChain = configs.DEFAULT_CHAIN;

    // Check network and switch only if necessary
    if (isWrongNetwork(defaultChain)) {
      await changeNetwork(defaultChain);
    }

    // Only proceed if the contract and account are available
    if (balanceSECONDContract && account) {
      try {
        const resultArray = await balanceSECONDContract.balanceOf(account);
        setBalanceSECOND(resultArray);
      } catch (error) {
        console.error("Error fetching balance:", error);
      }
    }
  };

  useEffect(() => {
    info();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, chainId, latestBlockNumber]);

  const router = useRouter();

  return (
    <header className="flex-shrink-0 w-full zBg-4 fixed z-50">
      <Popover as="nav" className="z-10 w-full bg-transparent ">
        {({ open }) => (
          <div className="lg:flex px-6 py-4">
            {/* <NavLink href="/">
              <a
                tabIndex={0}
                className="flex items-center pl-4 hidden lg:contents"
              >
                <Image
                  src="/images/logos/logo.png"
                  alt="MetaDOS"
                  width="133px"
                  height="96px"
                />
              </a>
            </NavLink> */}

            <NavLink href="/">
              <a
                tabIndex={0}
                className="flex items-center justify-center w-full lg:pl-2 lg:w-auto lg:contents hidden lg:block"
              >
                <div className="flex justify-center items-center">
                  <Image
                    src="/images/logos/logo.png"
                    alt="MetaDOS"
                    width={48}
                    height={48}
                    className="object-contain"
                  />
                </div>
              </a>
            </NavLink>

            <div className="flex items-center justify-between w-full px-2">
              <div className="flex items-center place-items-center">
                <div className="flex justify-center items-center lg:hidden">
                  <Image
                    src="/images/logos/logo.png"
                    alt="MetaDOS"
                    width="48"
                    height="48"
                    className="object-contain"
                  />
                </div>

                <div className="hidden lg:flex ">
                  <div className="flex space-x-2 place-items-center">
                    <NavLink
                      href="/"
                      active={
                        router.pathname === "/" || router.pathname === "/pool"
                      }
                    >
                      <a
                        id={"pool-nav-link"}
                        className={`py-1 no-underline text-sm hover:font-bold hover:zText-red-primary px-4 whitespace-nowrap ${(router.pathname === "/" || router.pathname === "/pool") && ""
                          }`}
                      >
                        {i18n._(t`Staking pools`)}
                      </a>
                    </NavLink>
                    <NavLink href="https://overmint.com/">
                      <a
                        id={"market-nav-link"}
                        target="_blank"
                        className="hover:zText-red-primary flex items-center py-1 no-underline text-sm zText-2 font-bold px-4 whitespace-nowrap place-items-center"
                      >
                        {i18n._(t`Marketplace`)}
                        <span className="pt-[2px] ml-1">
                          <ArrowIcon />
                        </span>
                      </a>
                    </NavLink>

                    <NavLink href="https://metados.com/">
                      <a
                        id={"game-nav-link"}
                        target="_blank"
                        className="hover:zText-red-primary flex items-center py-1 no-underline text-sm zText-2 font-bold px-4 whitespace-nowrap place-items-center"
                      >
                        {i18n._(t`Games`)}
                        <span className="pt-[2px] ml-1">
                          <ArrowIcon />
                        </span>
                      </a>
                    </NavLink>
                    <NavLink href="https://doschain.com/">
                      <a
                        id={"game-nav-link"}
                        target="_blank"
                        className="hover:zText-red-primary flex items-center py-1 no-underline text-sm zText-2 font-bold px-4 whitespace-nowrap place-items-center"
                      >
                        {i18n._(t`DOS Chain`)}
                        <span className="pt-[2px] ml-1">
                          <ArrowIcon />
                        </span>
                      </a>
                    </NavLink>
                    <NavLink href="https://bridge.doschain.com/">
                      <a
                        id={"game-nav-link"}
                        target="_blank"
                        className="hover:zText-red-primary flex items-center py-1 no-underline text-sm zText-2 font-bold px-4 whitespace-nowrap place-items-center"
                      >
                        {i18n._(t`Bridge`)}
                        <span className="pt-[2px] ml-1">
                          <ArrowIcon />
                        </span>
                      </a>
                    </NavLink>
                    <NavLink href="https://doscan.io/">
                      <a
                        id={"game-nav-link"}
                        target="_blank"
                        className="hover:zText-red-primary flex items-center py-1 no-underline text-sm zText-2 font-bold px-4 whitespace-nowrap place-items-center"
                      >
                        {i18n._(t`Explorer`)}
                        <span className="pt-[2px] ml-1">
                          <ArrowIcon />
                        </span>
                      </a>
                    </NavLink>
                  </div>
                </div>
              </div>

              {isLg && (
                // <div
                //   className=" left-0 z-50 flex flex-row items-center justify-center w-full py-4
                // lg:w-auto lg:relative lg:p-0 lg:bg-transparent"
                // >

                //   <div className="flex items-center justify-between w-full space-x-2 lg:justify-end">
                //     {/* <ThemeToggle /> */}

                //     {library && library.provider.isMetaMask && (
                //       <div className="hidden lg:inline-block" />
                //     )}
                //     {!isEmpty(convertBigNumberToNormal(balanceSECOND, 18)) && (
                //       <div
                //         className="w-auto flex items-center rounded-full px-0.5 
                //         whitespace-nowrap text-x-small font-bold select-none pointer-events-auto
                //         border-1"
                //         style={{ border: '1px solid #353535' }}
                //       >
                //         <div className="px-3 py-2 text-primary text-bold">
                //           {(isZeroBigNumber(balanceSECOND)
                //             ? 0
                //             : formatDollarAmount(
                //               convertBigNumberToNormal(balanceSECOND, 18),
                //               5
                //             )) + " SECOND"}
                //         </div>
                //       </div>
                //     )}
                //     <div
                //       className={`w-auto flex items-center rounded-full px-0.5 
                //       whitespace-nowrap text-x-small font-bold select-none pointer-events-auto
                //       border-1
                //       ${account && chainId && !isNil(etherBalance) && "pl-5"}`}
                //       style={{ border: '1px solid #353535' }}
                //     >
                //       {account &&
                //         chainId &&
                //         (!isNil(etherBalance) ? (
                //           <div className="px-3 py-2 text-primary text-bold">
                //             {truncate(
                //               convertBigNumberToNormal(etherBalance, 18),
                //               18
                //             ) || 0}{" "}
                //             {[ChainId.FUJI].includes(
                //               Number(chainId)
                //             )
                //               ? "AVAX"
                //               : "ETH"}
                //           </div>
                //         ) : (
                //           ""
                //         ))}
                //       <Web3Status />
                //     </div>
                //   </div>

                // </div>

                <div className="left-0 z-50 flex flex-row items-center justify-center w-full py-4 lg:w-auto lg:relative lg:p-0 lg:bg-transparent">
                  <div className="flex items-center justify-between w-full space-x-2 lg:justify-end">
                    {/* <ThemeToggle /> */}
                    {isMetaMask && <div className="hidden lg:inline-block" />}
                    {hasSecondBalance && (
                      <div
                        className="zBg-1 w-auto flex items-center rounded-[8px] px-0.5
              whitespace-nowrap text-x-small font-bold select-none pointer-events-auto
              border border-green-500"
                      >
                        <div className="flex items-center px-3 py-2 zText-1">
                          <div className="relative w-5 h-5 mr-2">
                            <Image src="/images/logos/logo-white.png" alt="Balance" layout="fill" objectFit="contain" />
                          </div>
                          {`${formatDollarAmount(convertBigNumberToNormal(balanceSECOND, 18), 5)} SECOND`}
                        </div>
                      </div>
                    )}
                    <div
                      className={`w-auto flex items-center px-0.5
            whitespace-nowrap text-x-small font-bold select-none pointer-events-auto ${hasEtherBalance ? 'pl-5' : ''}`}
                    >
                      {hasEtherBalance && (
                        <div className="flex items-center px-3 py-2 zText-1">
                          <div className="relative w-5 h-5 mr-2">
                            <Image src="/images/icons/wallet.svg" alt="Wallet" layout="fill" objectFit="contain" />
                          </div>
                          {truncate(convertBigNumberToNormal(etherBalance, 18), 18) || 0}{' '}
                          {[ChainId.AVAX].includes(Number(chainId)) ? 'AVAX' : 'ETH'}
                        </div>
                      )}
                      <Web3Status />
                    </div>
                  </div>
                </div>


              )}
              {!isLg && (
                <div className="flex -mr-2 lg:hidden">
                  <div
                    className={`w-auto flex items-center rounded-full
                      whitespace-nowrap text-x-small font-bold cursor-pointer select-none pointer-events-auto
                      }`}
                  // style={{ border: '1px solid #353535' }}
                  >
                    {!isEmpty(convertBigNumberToNormal(balanceSECOND, 18)) ? (
                      <div className="text-xs px-3 text-primary text-bold cursor-default">
                        {(isZeroBigNumber(balanceSECOND)
                          ? 0
                          : formatDollarAmount(
                            convertBigNumberToNormal(balanceSECOND, 18),
                            5
                          )) + " SECOND"}
                      </div>
                    ) : (
                      !isNil(balanceSECOND) && (
                        <div className="text-xs px-3 py-2 text-primary text-bold cursor-default">
                          0 SECOND
                        </div>
                      )
                    )}
                    <Web3Status />
                  </div>
                  <Popover.Button className="inline-flex items-center justify-center p-2 rounded-md text-primary hover:text-high-emphesis focus:outline-none">
                    <span className="sr-only">{i18n._(t`Open main menu`)}</span>
                    {open ? (
                      <svg
                        className="block w-6 h-6"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    ) : (
                      // <X title="Close" className="block w-6 h-6" aria-hidden="true" />
                      <svg
                        className="block w-6 h-6"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6h16M4 12h16M4 18h16"
                        />
                      </svg>
                      // <Burger title="Burger" className="block w-6 h-6" aria-hidden="true" />
                    )}
                  </Popover.Button>
                </div>
              )}
            </div>

            <Popover.Panel className="lg:hidden">
              <div className="flex flex-col px-4 pt-2 pb-3 space-y-1">
                <NavLink href="/" active={router.pathname === "/"}>
                  <a
                    id={"pool-nav-link-mobile"}
                    className="p-2 text-baseline text-color-2  hover:font-bold lg:p-3 whitespace-nowrap"
                  >
                    {i18n._(t`Staking pools`)}
                  </a>
                </NavLink>
                <NavLink href="https://overmint.com/">
                  <a
                    id={"market-nav-link-mobile"}
                    target="_blank"
                    className="p-2 text-baseline text-color-white  hover:font-bold lg:p-3 whitespace-nowrap"
                  >
                    {i18n._(t`Marketplace`)}
                  </a>
                </NavLink>
                <NavLink href="https://metados.io/">
                  <a
                    id={"game-nav-link-mobile"}
                    target="_blank"
                    className="p-2 text-baseline text-color-white  hover:font-bold lg:p-3 whitespace-nowrap"
                  >
                    {i18n._(t`Game play`)}
                  </a>
                </NavLink>
              </div>
            </Popover.Panel>
          </div>
        )}
      </Popover>
    </header>
  );
}

export default AppBar;
